/**
 * XLSX Export Utility
 * Exporta dados para Excel com formatação via HTML Table + MIME type
 * Compatível com Excel via upload direto ou conversão
 */
import * as XLSX from 'xlsx';
import i18n from '@/i18n';

export interface ExcelColumn {
  key: string;
  header: string;
  width?: number;
  format?: (value: any, row?: any) => string | number;
  alignment?: 'left' | 'center' | 'right';
  bold?: boolean;
}

function getLocaleCode(): string {
  return i18n.language?.startsWith("en") ? "en-US" : "pt-BR";
}

function formatMoney(v: number): string {
  const isEn = i18n.language?.startsWith("en");
  if (typeof v !== "number") return isEn ? "$0.00" : "R$ 0,00";
  return new Intl.NumberFormat(getLocaleCode(), {
    style: "currency",
    currency: isEn ? "USD" : "BRL",
  }).format(v);
}

/**
 * Funções de tradução dinâmicas para exportações
 */
export function translatePaymentMethod(method?: string | null): string {
  if (!method) return "—";
  const m = String(method).toLowerCase().trim();
  const isEn = i18n.language?.startsWith("en");

  if (m === "pix") return "PIX";
  if (m === "credit_card" || m === "creditcard" || m === "card") return isEn ? "Credit Card" : "Cartão de Crédito";
  if (m === "boleto") return "Boleto";
  if (m === "free" || m === "gratuito") return isEn ? "Free" : "Gratuito";
  if (m === "manual") return isEn ? "Manual / Complimentary" : "Manual / Cortesia";
  return method;
}

export function translateOrderStatus(status?: string | null, paymentMethod?: string | null, total?: number | null): string {
  if (!status) return "—";
  const s = String(status).toLowerCase().trim();
  const m = paymentMethod ? String(paymentMethod).toLowerCase().trim() : "";
  const isEn = i18n.language?.startsWith("en");

  if (s === "paid" || s === "approved" || s === "completed") {
    if (m === "free" || total === 0 || m === "gratuito") {
      return isEn ? "Approved" : "Aprovado";
    }
    return isEn ? "Paid" : "Pago";
  }
  if (s === "free_approved") return isEn ? "Approved" : "Aprovado";
  if (s === "pending" || s === "boleto_pending") return isEn ? "Pending" : "Pendente";
  if (s === "processing") return isEn ? "Processing" : "Processando";
  if (s === "cancelled" || s === "canceled") return isEn ? "Canceled" : "Cancelado";
  if (s === "refunded") return isEn ? "Refunded" : "Estornado";
  if (s === "expired") return isEn ? "Expired" : "Expirado";
  if (s === "active") return isEn ? "Active" : "Ativo";
  return status;
}

export function translateTicketStatus(status?: string | null): string {
  if (!status) return "—";
  const s = String(status).toLowerCase().trim();
  const isEn = i18n.language?.startsWith("en");

  if (s === "active") return isEn ? "Active" : "Ativo";
  if (s === "used" || s === "checked_in") return isEn ? "Checked-in" : "Utilizado";
  if (s === "reserved") return isEn ? "Reserved" : "Reservado";
  if (s === "cancelled" || s === "canceled") return isEn ? "Canceled" : "Cancelado";
  if (s === "transferred") return isEn ? "Transferred" : "Transferido";
  if (s === "expired") return isEn ? "Expired" : "Expirado";
  return status;
}

export function translateEventStatus(status?: string | null, visibility?: string | null): string {
  if (!status) return "—";
  const s = String(status).toLowerCase().trim();
  const vis = String(visibility || "").toLowerCase().trim();
  const isEn = i18n.language?.startsWith("en");

  if (s === "draft") return isEn ? "Draft" : "Rascunho";
  if (s === "cancelled" || s === "canceled") return isEn ? "Canceled" : "Cancelado";
  if (s === "finished" || s === "ended" || s === "completed" || s === "done") return isEn ? "Ended" : "Finalizado";
  if (vis === "private" || s === "private") return isEn ? "Private" : "Privado";
  if (s === "published") return isEn ? "Published" : "Publicado";
  return status;
}

export async function exportToXLSX<T extends Record<string, any>>(
  data: T[],
  columns: ExcelColumn[],
  filename: string,
  options?: { appendDate?: boolean }
) {
  const appendDate = options?.appendDate ?? true;
  if (!data || data.length === 0) return;

  try {
    // Preparar dados
    const headers = columns.map((c) => c.header);
    const rows = data.map((row) =>
      columns.map((col) => {
        const raw = col.key.includes(".")
          ? col.key.split(".").reduce((o: any, k) => o?.[k], row)
          : row[col.key];
        const value = col.format ? col.format(raw, row) : raw;
        return value == null ? "" : value;
      })
    );

    // Criar worksheet
    const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);

    // Aplicar formatação ao header (linha 1)
    for (let col = 0; col < headers.length; col++) {
      const cellRef = XLSX.utils.encode_col(col) + '1';
      if (!worksheet[cellRef]) {
        worksheet[cellRef] = { t: 's', v: headers[col] };
      }
      worksheet[cellRef].s = {
        font: { bold: true, color: { rgb: "FFFFFFFF" }, size: 11 },
        fill: { fgColor: { rgb: "FF4F81BD" } },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "FF000000" } },
          bottom: { style: "thin", color: { rgb: "FF000000" } },
          left: { style: "thin", color: { rgb: "FF000000" } },
          right: { style: "thin", color: { rgb: "FF000000" } },
        },
      };
    }

    // Aplicar bordas ao resto das linhas
    const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_col(col) + (row + 1);
        if (worksheet[cellRef]) {
          worksheet[cellRef].s = {
            border: {
              top: { style: "thin", color: { rgb: "FFD9D9D9" } },
              bottom: { style: "thin", color: { rgb: "FFD9D9D9" } },
              left: { style: "thin", color: { rgb: "FFD9D9D9" } },
              right: { style: "thin", color: { rgb: "FFD9D9D9" } },
            },
            alignment: {
              horizontal: (columns[col]?.alignment || 'left') as any,
              vertical: "center",
            },
          };
        }
      }
    }

    // Definir largura das colunas
    worksheet['!cols'] = columns.map((col) => ({
      wch: col.width || Math.min(col.header.length + 2, 30),
    }));

    // Criar workbook
    const workbook = XLSX.utils.book_new();
    const sheetName = i18n.language?.startsWith("en") ? "Data" : "Dados";
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Fazer download
    const finalName = appendDate
      ? `${filename}_${new Date().toISOString().slice(0, 10)}.xlsx`
      : `${filename}.xlsx`;
    XLSX.writeFile(workbook, finalName);
  } catch (error) {
    console.error("Erro ao exportar XLSX:", error);
    throw error;
  }
}

// Presets de colunas para tabelas comuns com tradução dinâmica
export function getOrderXLSXColumns(): ExcelColumn[] {
  const isEn = i18n.language?.startsWith("en");
  const locale = getLocaleCode();
  return [
    { key: "id", header: "ID", width: 12 },
    { key: "profiles.full_name", header: isEn ? "Buyer" : "Comprador", width: 22, format: (v: any, row: any) => v || row?.buyer_name || row?.user_name || "—" },
    { key: "events.title", header: isEn ? "Event" : "Evento", width: 25, format: (v: any, row: any) => v || row?.event_title || "—" },
    { key: "total", header: "Total", width: 14, format: (v: number) => formatMoney(v), alignment: "right" },
    { key: "platform_fee", header: isEn ? "Platform Fee" : "Taxa Plataforma", width: 16, format: (v: number) => formatMoney(v), alignment: "right" },
    { key: "payment_method", header: isEn ? "Payment Method" : "Método de Pagamento", width: 20, format: (v: string) => translatePaymentMethod(v) },
    { key: "status", header: "Status", width: 14, format: (v: string, row: any) => translateOrderStatus(v, row?.payment_method, row?.total) },
    { key: "created_at", header: isEn ? "Order Date" : "Data do Pedido", width: 20, format: (v: string) => v ? new Date(v).toLocaleDateString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
  ];
}

export function getUserXLSXColumns(): ExcelColumn[] {
  const isEn = i18n.language?.startsWith("en");
  const locale = getLocaleCode();
  return [
    { key: "full_name", header: isEn ? "Name" : "Nome", width: 25 },
    { key: "email", header: "E-mail", width: 28, format: (v: any) => v || "—" },
    { key: "document_number", header: isEn ? "Document" : "Documento", width: 15 },
    { key: "phone", header: isEn ? "Phone" : "Telefone", width: 15 },
    { key: "last_sign_in_at", header: isEn ? "Last Sign In" : "Último acesso", width: 20, format: (v: string) => v ? new Date(v).toLocaleString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
    { key: "created_at", header: isEn ? "Created At" : "Cadastro", width: 18, format: (v: string) => v ? new Date(v).toLocaleDateString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
  ];
}

export function getTicketXLSXColumns(): ExcelColumn[] {
  const isEn = i18n.language?.startsWith("en");
  const locale = getLocaleCode();
  return [
    { key: "id", header: isEn ? "Ticket ID" : "ID Ingresso", width: 14 },
    { key: "attendee_name", header: isEn ? "Attendee" : "Participante", width: 25, format: (v: any) => v || "—" },
    { key: "attendee_email", header: isEn ? "Attendee Email" : "Email Participante", width: 25, format: (v: any) => v || "—" },
    { key: "attendee_cpf", header: isEn ? "Attendee Document" : "CPF Participante", width: 18, format: (v: string) => v ? v.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4") : "—" },
    { key: "attendee_phone", header: isEn ? "Attendee Phone" : "Tel. Participante", width: 18, format: (v: any) => v || "—" },
    { key: "profiles.full_name", header: isEn ? "Buyer" : "Comprador", width: 22, format: (v: any, row: any) => v || row?.orders?.profiles?.full_name || "—" },
    { key: "ticket_tiers.name", header: isEn ? "Ticket Tier" : "Lote / Tipo", width: 18, format: (v: any, row: any) => v || row?.ticket_tiers?.name || "—" },
    { key: "status", header: "Status", width: 14, format: (v: string) => translateTicketStatus(v) },
    { key: "checked_in_at", header: isEn ? "Check-in Date" : "Data Check-in", width: 20, format: (v: string) => v ? new Date(v).toLocaleString(locale, { timeZone: "America/Sao_Paulo" }) : (isEn ? "Not checked-in" : "Não realizado") },
    { key: "created_at", header: isEn ? "Issuance Date" : "Data Emissão", width: 20, format: (v: string) => v ? new Date(v).toLocaleDateString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
  ];
}

export function getEventXLSXColumns(): ExcelColumn[] {
  const isEn = i18n.language?.startsWith("en");
  const locale = getLocaleCode();
  return [
    { key: "title", header: isEn ? "Title" : "Título", width: 28 },
    { key: "slug", header: "Slug", width: 22 },
    { key: "status", header: "Status", width: 14, format: (v: string, row?: any) => translateEventStatus(v, row?.visibility) },
    { key: "venue_city", header: isEn ? "City" : "Cidade", width: 16, format: (v: any) => v || "—" },
    { key: "start_date", header: isEn ? "Start Date" : "Data de Início", width: 20, format: (v: string) => v ? new Date(v).toLocaleDateString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
    { key: "end_date", header: isEn ? "End Date" : "Data de Término", width: 20, format: (v: string) => v ? new Date(v).toLocaleDateString(locale, { timeZone: "America/Sao_Paulo" }) : "—" },
  ];
}

// Retrocompatibilidade usando getters dinamicos
export const orderXLSXColumns = new Proxy([], {
  get(_, prop) {
    const cols = getOrderXLSXColumns();
    return (cols as any)[prop];
  }
}) as unknown as ExcelColumn[];

export const userXLSXColumns = new Proxy([], {
  get(_, prop) {
    const cols = getUserXLSXColumns();
    return (cols as any)[prop];
  }
}) as unknown as ExcelColumn[];

export const ticketXLSXColumns = new Proxy([], {
  get(_, prop) {
    const cols = getTicketXLSXColumns();
    return (cols as any)[prop];
  }
}) as unknown as ExcelColumn[];

export const eventXLSXColumns = new Proxy([], {
  get(_, prop) {
    const cols = getEventXLSXColumns();
    return (cols as any)[prop];
  }
}) as unknown as ExcelColumn[];
